import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

/**
 * Dictation that shows the words as they are spoken.
 *
 * The hook owns the text. It keeps a `settled` ref of everything already
 * finalized and re-renders `settled + interim` on every partial result, so the
 * textarea fills live instead of jumping at the end. Same shape as the
 * goalwizard `useSpeech` hook this is lifted from.
 *
 * Two engines, because no single one covers both targets:
 *  - Native (iOS / Android): `@capacitor-community/speech-recognition`, which
 *    streams `partialResults` off the on-device recognizer. The plugin's own
 *    web implementation throws on every method, so it is only used natively.
 *  - Web: the browser Speech API, for Chrome and mobile Safari.
 *
 * Both are free and on-device. If neither exists, `supported` is false and the
 * caller just shows a plain textarea. Typing is never taken away, and typing
 * mid-dictation composes correctly because `setText` re-seats `settled`.
 */

const getWebRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

const DECLINED = 'Microphone access was declined. You can still type.';
const FAILED = 'Dictation stopped unexpectedly. You can still type.';

const join = (settled, interim) => `${settled}${interim}`.replace(/\s+/g, ' ').trim();

const useSpeechToText = (initialText = '') => {
  const [text, setTextState] = useState(initialText);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const [nativeReady, setNativeReady] = useState(false);

  const settled = useRef(initialText ? `${initialText} ` : '');
  const recognitionRef = useRef(null);
  const listenerRef = useRef(null);

  const native = isNative();
  const WebRecognition = getWebRecognition();
  const supported = native ? nativeReady : Boolean(WebRecognition);

  // Ask the device once whether it can actually do this.
  useEffect(() => {
    if (!native) return undefined;
    let cancelled = false;
    SpeechRecognition.available()
      .then(res => { if (!cancelled) setNativeReady(Boolean(res?.available)); })
      .catch(() => { if (!cancelled) setNativeReady(false); });
    return () => { cancelled = true; };
  }, [native]);

  /** Typing re-seats the settled buffer so speech appends after it. */
  const setText = useCallback((value) => {
    settled.current = value ? `${value} ` : '';
    setTextState(value);
  }, []);

  const clear = useCallback(() => {
    settled.current = '';
    setTextState('');
  }, []);

  // ---------------------------------------------------------------- web
  useEffect(() => {
    if (native || !WebRecognition) return undefined;

    const r = new WebRecognition();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';

    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) settled.current += `${chunk} `;
        else interim += chunk;
      }
      setTextState(join(settled.current, interim));
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setError(DECLINED);
      else if (e.error !== 'aborted' && e.error !== 'no-speech') setError(FAILED);
      setListening(false);
    };
    r.onend = () => setListening(false);

    recognitionRef.current = r;
    return () => {
      try { r.stop(); } catch (e) { /* already stopped */ }
      recognitionRef.current = null;
    };
  }, [native, WebRecognition]);

  // ------------------------------------------------------------- native
  const startNative = useCallback(async () => {
    const perm = await SpeechRecognition.requestPermissions().catch(() => null);
    if (perm && perm.speechRecognition && perm.speechRecognition !== 'granted') {
      setError(DECLINED);
      return;
    }

    // matches[0] is the whole utterance so far, not a delta, so it replaces the
    // interim tail rather than appending to it.
    listenerRef.current = SpeechRecognition.addListener('partialResults', (data) => {
      const partial = Array.isArray(data?.matches) ? data.matches[0] : '';
      if (partial) setTextState(join(settled.current, partial));
    });

    try {
      await SpeechRecognition.start({ partialResults: true, popup: false, language: 'en-US' });
      setListening(true);
    } catch (e) {
      setError(FAILED);
      setListening(false);
    }
  }, []);

  const stopNative = useCallback(async () => {
    try { await SpeechRecognition.stop(); } catch (e) { /* already stopped */ }
    if (listenerRef.current) {
      try { (await listenerRef.current).remove(); } catch (e) { /* gone */ }
      listenerRef.current = null;
    }
    // Promote whatever is on screen to settled so the next run appends to it.
    setTextState((current) => {
      settled.current = current ? `${current} ` : '';
      return current;
    });
    setListening(false);
  }, []);

  // ------------------------------------------------------------- public
  const start = useCallback(async () => {
    if (listening) return;
    setError(null);
    if (native) {
      await startNative();
      return;
    }
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (e) { /* already running */ }
  }, [native, listening, startNative]);

  const stop = useCallback(async () => {
    if (native) {
      await stopNative();
      return;
    }
    try { recognitionRef.current?.stop(); } catch (e) { /* fine */ }
    setListening(false);
  }, [native, stopNative]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Never leave the mic open behind a screen change.
  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => () => { stopRef.current(); }, []);

  return { supported, listening, text, setText, clear, error, start, stop, toggle };
};

export default useSpeechToText;
